import { lazy, Suspense } from "react";
import { Navigate, Outlet } from "react-router-dom";
// Temporarily disabled while usage billing is open. Restore with the wrapper below.
import { UsageGuard } from "src/guards/usage-guard";
import { ErrorGuard } from "src/guards/error-guard";
import { Layout as DashboardLayout } from "src/layouts/dashboard";
import { Layout as MarketingLayout } from "src/layouts/marketing";
import { AuthGuard } from "src/guards/auth-guard";
import { paths } from "src/paths";

// Test
const TestPage = lazy(() => import("src/pages/dashboard/test/test"));
const TestMarketWeeklyAdvanced = lazy(
  () => import("src/pages/dashboard/test/market-weekly-advanced"),
);
const TestMixedGraphPage = lazy(
  () => import("src/pages/dashboard/test/graphs/mixed"),
);
const TestScatterGraphPage = lazy(
  () => import("src/pages/dashboard/test/graphs/scatter-plot"),
);
const TestNivoLineGraphPage = lazy(
  () => import("src/pages/dashboard/test/graphs/nivo-line"),
);

const TestHighchartTileMapPage = lazy(
  () => import("src/pages/dashboard/test/graphs/highchart-tilemap"),
);

const TestHighchartHeatMapPage = lazy(
  () => import("src/pages/dashboard/test/graphs/highchart-heatmap"),
);

const TestHighchartSyncronizedPage = lazy(
  () => import("src/pages/dashboard/test/graphs/highchart-syncronized"),
);

const TestHighchartOverlayColumnsPage = lazy(
  () => import("src/pages/dashboard/test/graphs/highchart-overlay-columns"),
);

const TestRegionFilterPage = lazy(
  () => import("src/pages/dashboard/test/region-filter"),
);

const TestFullLayoutPage = lazy(
  () => import("src/pages/dashboard/test/full-layout"),
);

const TestDataLakePage = lazy(
  () => import("src/pages/dashboard/test/test-datalake"),
);

const TestGeoMap = lazy(() => import("src/pages/dashboard/test/map/geo-map"));
const TestNaverMap = lazy(
  () => import("src/pages/dashboard/test/map/naver-map"),
);
const TestRegionMap = lazy(
  () => import("src/pages/dashboard/test/map/region-map"),
);
const TestGunguGroupFilter = lazy(
  () => import("src/pages/dashboard/test/gungu-group-filter"),
);
const TestCustomTable = lazy(
  () => import("src/pages/dashboard/test/custom-table"),
);

const TestDataTable = lazy(() => import("src/pages/dashboard/test/datatable"));

const AdminCollectorList = lazy(
  () => import("src/pages/dashboard/admin/collector-list"),
);

const AdminServerLogsPage = lazy(
  () => import("src/pages/dashboard/admin/server-logs"),
);
const AdminFailsList = lazy(
  () => import("src/pages/dashboard/admin/faileds-list"),
);
const AdminUserList = lazy(() => import("src/pages/dashboard/admin/user-list"));

const AdminCollectionInfo = lazy(
  () => import("src/pages/dashboard/admin/collection-info"),
);

const AdminAptMatch = lazy(
  () => import("src/pages/dashboard/admin/matchs/apt-match"),
);

const AdminSystemDashboard = lazy(
  () => import("src/pages/dashboard/admin/system-dashboard"),
);

const AdminBlogManage = lazy(
  () => import("src/pages/dashboard/admin/blog-manage"),
);

const AdminCouponManage = lazy(
  () => import("src/pages/dashboard/admin/coupon-manage"),
);

const AdminNoticeManage = lazy(
  () => import("src/pages/dashboard/admin/notice-manage"),
);
const AdminCommunityManage = lazy(
  () => import("src/pages/dashboard/admin/community-manage"),
);
const AdminPriceOutliers = lazy(
  () => import("src/pages/dashboard/admin/price-outlier-manage"),
);
const AdminDatalakeCollector = lazy(
  () => import("src/pages/dashboard/admin/datalake-collector"),
);
const AdminStocksManage = lazy(
  () => import("src/pages/dashboard/admin/stocks-manage"),
);
const AdminRegionMatch = lazy(
  () => import("src/pages/dashboard/admin/region-match"),
);
const AdminMismatchManage = lazy(
  () => import("src/pages/dashboard/admin/mismatch-manage"),
);

// Apts
const AptAnalyze = lazy(() => import("src/pages/dashboard/apts/apt-analyze"));
const AptInfo = lazy(() => import("src/pages/dashboard/apts/apt-info"));
const AptInfoNewWindow = lazy(
  () => import("src/pages/dashboard/apts/apt-info-newwindow"),
);
const PyeongInfoNewWindow = lazy(
  () => import("src/pages/dashboard/apts/pyeong-info-newwindow"),
);
const AptSelect = lazy(
  () => import("src/pages/dashboard/aptsselect/apts-select"),
);
const AptUndervalue = lazy(
  () => import("src/pages/dashboard/aptsselect/apts-undervalue"),
);
const AptBargain = lazy(
  () => import("src/pages/dashboard/aptsselect/apts-bargain"),
);
const AptNewHighDrop = lazy(
  () => import("src/pages/dashboard/aptsselect/apts-new-high-drop"),
);
const AptSelectMap = lazy(
  () => import("src/pages/dashboard/aptsselect/apts-select-map"),
);

const AptGroup = lazy(
  () => import("src/pages/dashboard/aptsselect/apts-group"),
);

// Condition
const NationalRankingBoard = lazy(
  () => import("src/pages/dashboard/condition/national-ranking-board"),
);

const MainRegionIndex = lazy(
  () => import("src/pages/dashboard/condition/main-region-index"),
);

const MainRegionMonthly = lazy(
  () => import("src/pages/dashboard/condition/main-region-monthly"),
);

const MarketOpportunityStatus = lazy(
  () => import("src/pages/dashboard/condition/market-opportunity-status"),
);

// SIDO
const SidoMainIndex = lazy(
  () => import("src/pages/dashboard/condition/sido-mainindex"),
);

const SidoPriceIndexCondition = lazy(
  () => import("src/pages/dashboard/condition/sido-priceindex"),
);
const SidoMarketIndexCondition = lazy(
  () => import("src/pages/dashboard/condition/sido-marketindex"),
);
const SidoTimeSeries = lazy(
  () => import("src/pages/dashboard/condition/sido-timeseries"),
);

// GUNGU
const GunguMainIndex = lazy(
  () => import("src/pages/dashboard/condition/gungu-mainindex"),
);
const GunguTimeSeries = lazy(
  () => import("src/pages/dashboard/condition/gungu-timeseries"),
);

// Analyze
const SidoAnalyze = lazy(
  () => import("src/pages/dashboard/analyze/sido-analyze"),
);
const GunguAnalyze = lazy(
  () => import("src/pages/dashboard/analyze/gungu-analyze"),
);
const MultiAnalyze = lazy(
  () => import("src/pages/dashboard/analyze/multi-analyze"),
);
const AnalyzeList = lazy(
  () => import("src/pages/dashboard/analyze/analyze-list"),
);
const AnalysisNotes = lazy(
  () => import("src/pages/dashboard/analyze/analysis-notes"),
);
const AnalysisNoteNewWindow = lazy(
  () => import("src/pages/dashboard/analyze/analysis-note-newwindow"),
);
const AnalysisNoteFolderWindow = lazy(
  () => import("src/pages/dashboard/analyze/analysis-note-folder-window"),
);
const AnalyzeNewWindow = lazy(
  () => import("src/pages/dashboard/analyze/multi-analyze-newwindow"),
);
const RegionInfo2 = lazy(() => import("src/pages/dashboard/regions/region-info2"));
const RegionInfoNewWindow = lazy(() => import("src/pages/dashboard/regions/region-info-newwindow"));
const RegionInfo = lazy(
  () => import("src/pages/dashboard/regions/region-info"),
);
const RegionFavorite = lazy(
  () => import("src/pages/dashboard/regions/region-favorite"),
);

// RegionAnalyze는 RegionInfo에 통합됨

const MapAnalyze = lazy(
  () => import("src/pages/dashboard/manalyze/map-analyze"),
);

const CycleAnalyze = lazy(
  () => import("src/pages/dashboard/condition/cycle-analyze"),
);
const RegionCompare = lazy(
  () => import("src/pages/dashboard/ranalyze/region-compare"),
);
const RiskMonitor = lazy(
  () => import("src/pages/dashboard/ranalyze/risk-monitor"),
);
const Screening = lazy(
  () => import("src/pages/dashboard/ranalyze/screening"),
);
const PriceDistribution = lazy(
  () => import("src/pages/dashboard/ranalyze/price-distribution"),
);
const DashboardDemo = lazy(() => import("src/pages/dashboard/demo"));
const UsageOrder = lazy(() => import("src/pages/dashboard/pricing/order"));
const LayoutSettings = lazy(
  () => import("src/pages/dashboard/settings/layout-settings"),
);
const MyPage = lazy(() => import("src/pages/dashboard/mypage"));
const CommunityPage = lazy(() => import("src/pages/dashboard/community"));
const UsageGuidePage = lazy(() => import("src/pages/dashboard/guide/usage-guide"));
const DataSourcesPage = lazy(() => import("src/pages/dashboard/guide/data-sources"));

export const dashboardRoutes = [
  // pricing은 전체 페이지 (로그인 필수, MarketingLayout: 로고 + 유저 정보)
  {
    path: "dashboard/pricing",
    element: (
      <AuthGuard>
        <MarketingLayout>
          <Suspense>
            <UsageOrder />
          </Suspense>
        </MarketingLayout>
      </AuthGuard>
    ),
  },
  {
    path: "dashboard/mypage",
    element: (
      <DashboardLayout>
        <ErrorGuard>
          <Suspense>
            <MyPage />
          </Suspense>
        </ErrorGuard>
      </DashboardLayout>
    ),
  },
  {
    path: "dashboard/community",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <ErrorGuard>
            <Suspense><CommunityPage /></Suspense>
          </ErrorGuard>
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "dashboard",
    element: (
      <DashboardLayout>
        <ErrorGuard>
          <Suspense>
            <UsageGuard>
            <Outlet />
            </UsageGuard>
          </Suspense>
        </ErrorGuard>
      </DashboardLayout>
    ),
    children: [
      {
        path: "guide/proplake",
        element: <UsageGuidePage />,
      },
      {
        path: "guide/data-sources",
        element: <DataSourcesPage />,
      },
      {
        index: true,
        element: (
          <Navigate
            to={paths.dashboard.index}
            replace
          />
        ),
      },
      {
        path: "home",
        element: <DashboardDemo />,
      },
      {
        path: "condition",
        children: [
          {
            path: "nation-ranking",
            element: <NationalRankingBoard />,
          },
          {
            path: "mainregion-index",
            element: <MainRegionIndex />,
          },
          {
            path: "mainregion-monthly",
            element: <MainRegionMonthly />,
          },
          {
            path: "market-opportunity",
            element: <MarketOpportunityStatus />,
          },
          {
            path: "sido-mainindex",
            element: <SidoMainIndex />,
          },
          {
            path: "sido-priceindex",
            element: <SidoPriceIndexCondition />,
          },
          {
            path: "sido-marketindex",
            element: <SidoMarketIndexCondition />,
          },
          {
            path: "sido-timeseries",
            element: <SidoTimeSeries />,
          },
          {
            path: "gungu-mainindex",
            element: <GunguMainIndex />,
          },
          {
            path: "gungu-timeseries",
            element: <GunguTimeSeries />,
          },
          {
            path: "cycle",
            element: <CycleAnalyze />,
          },
        ],
      },
      {
        path: "regions",
        children: [
          {
            path: "favorite",
            element: <RegionFavorite />,
          },
          {
            path: "info",
            element: <RegionInfo />,
          },
          {
            path: "info2",
            element: <RegionInfo2 />,
          },
          {
            path: "newwindow",
            element: <RegionInfoNewWindow />,
          },
        ],
      },
      {
        path: "mregion",
        children: [
          {
            path: "analyze",
            element: <MapAnalyze />,
          },
        ],
      },
      {
        path: "rregion",
        children: [
          {
            path: "analyze",
            element: (
              <Navigate
                to="/dashboard/regions/info"
                replace
              />
            ),
          },
          {
            path: "screening",
            element: <Screening />,
          },
          {
            path: "compare",
            element: <RegionCompare />,
          },
          {
            path: "risk",
            element: <RiskMonitor />,
          },
          {
            path: "price-distribution",
            element: <PriceDistribution />,
          },
        ],
      },
      {
        path: "analyze",
        children: [
          {
            path: "sido",
            element: <SidoAnalyze />,
          },
          {
            path: "gungu",
            element: <GunguAnalyze />,
          },
          {
            path: "multi",
            element: <MultiAnalyze />,
          },
          {
            path: "list",
            element: <AnalyzeList />,
          },
          {
            path: "notes",
            element: <AnalysisNotes />,
          },
          {
            path: "note-newwindow",
            element: <AnalysisNoteNewWindow />,
          },
          {
            path: "note-folder-window",
            element: <AnalysisNoteFolderWindow />,
          },
          {
            path: "newwindow",
            element: <AnalyzeNewWindow />,
          },
        ],
      },
      {
        path: "apts",
        children: [
          {
            index: true,
            element: <AptAnalyze />,
          },
          {
            path: "info",
            element: <AptInfo />,
          },
          {
            path: "select",
            element: <AptSelect />,
          },
          {
            path: "undervalue",
            element: <AptUndervalue />,
          },
          {
            path: "upside",
            element: <AptUndervalue fixedMode="upside" />,
          },
          {
            path: "bargain",
            element: <AptBargain />,
          },
          {
            path: "new-high-drop",
            element: <AptNewHighDrop />,
          },
          {
            path: "selectmap",
            element: <AptSelectMap />,
          },
          {
            path: "group",
            element: <AptGroup />,
          },

          {
            path: "newwindow",
            element: <AptInfoNewWindow />,
          },
          {
            path: "pyeong-newwindow",
            element: <PyeongInfoNewWindow />,
          },
        ],
      },
      {
        path: "admin",
        children: [
          {
            path: "collector-list",
            element: <AdminCollectorList />,
          },
          {
            path: "server-logs",
            element: <AdminServerLogsPage />,
          },
          {
            path: "fails",
            element: <AdminFailsList />,
          },
          {
            path: "userlist",
            element: <AdminUserList />,
          },
          {
            path: "collection-info",
            element: <AdminCollectionInfo />,
          },
          {
            path: "apt-match",
            element: <AdminAptMatch />,
          },
          {
            path: "system",
            element: <AdminSystemDashboard />,
          },
          {
            path: "blog",
            element: <AdminBlogManage />,
          },
          {
            path: "coupons",
            element: <AdminCouponManage />,
          },
          {
            path: "notices",
            element: <AdminNoticeManage />,
          },
          {
            path: "community",
            element: <AdminCommunityManage />,
          },
          {
            path: "recommendation-themes",
            element: <Navigate to={`${paths.dashboard.analyze.notes}?scope=theme`} replace />,
          },
          {
            path: "price-outliers",
            element: <AdminPriceOutliers />,
          },
          {
            path: "datalake-collector",
            element: <AdminDatalakeCollector />,
          },
          {
            path: "stocks",
            element: <AdminStocksManage />,
          },
          {
            path: "region-match",
            element: <AdminRegionMatch />,
          },
          {
            path: "mismatches",
            element: <AdminMismatchManage />,
          },
        ],
      },
      {
        path: "settings",
        children: [
          {
            path: "layout",
            element: <LayoutSettings />,
          },
        ],
      },
      {
        path: "test",
        children: [
          {
            index: true,
            element: <TestPage />,
          },
          {
            path: "market-weekly-advanced",
            element: <TestMarketWeeklyAdvanced />,
          },
          {
            path: "graph",
            children: [
              {
                path: "mixed",
                element: <TestMixedGraphPage />,
              },
              {
                path: "scatter",
                element: <TestScatterGraphPage />,
              },
              {
                path: "nivo-line",
                element: <TestNivoLineGraphPage />,
              },
              {
                path: "highchart-tilemap",
                element: <TestHighchartTileMapPage />,
              },
              {
                path: "highchart-heatmap",
                element: <TestHighchartHeatMapPage />,
              },
              {
                path: "highchart-syncronized",
                element: <TestHighchartSyncronizedPage />,
              },
              {
                path: "highchart-overlay-columns",
                element: <TestHighchartOverlayColumnsPage />,
              },
            ],
          },
          {
            path: "map",
            children: [
              {
                path: "geo-map",
                element: <TestGeoMap />,
              },
              {
                path: "region-map",
                element: <TestRegionMap />,
              },
              {
                path: "naver-map",
                element: <TestNaverMap />,
              },
            ],
          },
          {
            path: "component",
            children: [
              {
                path: "region-filter",
                element: <TestRegionFilterPage />,
              },
              {
                path: "gungu-group-filter",
                element: <TestGunguGroupFilter />,
              },
              {
                path: "test-datalake",
                element: <TestDataLakePage />,
              },
              {
                path: "custom-table",
                element: <TestCustomTable />,
              },
              {
                path: "datatable",
                element: <TestDataTable />,
              },
            ],
          },
          {
            path: "layout",
            children: [
              {
                path: "full-layout",
                element: <TestFullLayoutPage />,
              },
            ],
          },
        ],
      },
    ],
  },
];
